"use client";

import React, { useEffect, useMemo } from "react";
import {
  Chip,
  ListItem,
  Checkbox,
  Typography,
} from "@mui/material";
import { CustomFormLabel } from "../formsComponents";
import { EnrAutoComplete } from "../basic/EnrAutoComplete";
import { api } from "../../api/api";
import { IEvent } from "../../redux/event";
import { useBillingCompanyContext } from "../../providers";

export interface IPreferedEvent {
  event_uuid: string;
  event_name: string;
  event_type: string;
}

export interface IEventAutoSearchProps {
  label?: string;
  heading?: string;
  disabled?: boolean;
  error?: string | boolean;
  helperText?: string;
  fullWidth?: boolean;
  multiple?: boolean;
  value: string | IPreferedEvent | IPreferedEvent[];
  onSelect: (
    value: string | IPreferedEvent[],
    event_uuid?: string,
    event_type?: string,
  ) => void;
  isHighlighted?: boolean;
  allowedEvents?: string[];
}

// Special option for "Select All"
const SELECT_ALL_OPTION: IEvent = {
  event_uuid: "__SELECT_ALL__",
  event_name: "✓ Select All Events",
  event_description: "",
  status: "ACTIVE",
  company_outlet_uuid: "",
  created_by_uuid: "",
  created_by_name: "",
  modified_by_uuid: "",
  modified_by_name: "",
  create_ts: "",
  insert_ts: "",
};

export const EventAutoSearch: React.FC<IEventAutoSearchProps> = (props) => {
  const {
    value,
    onSelect,
    label,
    disabled,
    fullWidth,
    heading,
    multiple = false,
    error,
    helperText,
    isHighlighted = false,
  } = props;

  const [options, setOptions] = React.useState<readonly IEvent[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);

  const company = useBillingCompanyContext();

  const displayOptions = useMemo(() => {
    let finalOptions = [...options];

    if (!multiple && typeof value === "string" && value) {
      const exists = finalOptions.some(
        (opt) => opt.event_name.toLowerCase() === value.toLowerCase(),
      );

      if (!exists) {
        finalOptions = [
          {
            event_uuid: "__PREFILLED__",
            event_name: value,
            event_description: "",
            status: "ACTIVE",
            company_outlet_uuid: "",
            created_by_uuid: "",
            created_by_name: "",
            modified_by_uuid: "",
            modified_by_name: "",
            create_ts: "",
            insert_ts: "",
          },
          ...finalOptions,
        ];
      }
    }

    // For multiple mode, include pre-selected events in options so they display before API is called
    if (multiple && Array.isArray(value) && value.length > 0) {
      const preSelectedEvents = (value as IPreferedEvent[]).map((event) => ({
        event_uuid: event.event_uuid,
        event_name: event.event_name,
        event_description: "",
        status: "ACTIVE" as const,
        company_outlet_uuid: "",
        created_by_uuid: "",
        created_by_name: "",
        modified_by_uuid: "",
        modified_by_name: "",
        create_ts: "",
        insert_ts: "",
      }));

      // Add pre-selected events that don't already exist in options
      preSelectedEvents.forEach((preSelected) => {
        const exists = finalOptions.some(
          (opt) => opt.event_uuid === preSelected.event_uuid,
        );
        if (!exists) {
          finalOptions.push(preSelected);
        }
      });
    }

    if (multiple && finalOptions.length > 0) {
      return [SELECT_ALL_OPTION, ...finalOptions];
    }

    return finalOptions;
  }, [options, value, multiple]);

  // Convert value to the format needed for Autocomplete
  const getAutocompleteValue = () => {
    if (multiple) {
      // For multiple selection, value is IPreferedEvent[]
      const preferredEvents = value as IPreferedEvent[];
      if (!preferredEvents || preferredEvents.length === 0) return [];

      return displayOptions?.filter((opt) =>
        preferredEvents?.some((val) => val.event_uuid === opt.event_uuid),
      );
    } else {
      // For single selection, value can be string (event_name) or IPreferedEvent
      if (typeof value === "string") {
        // Find the event by name
        return (
          displayOptions?.find(
            (opt) => opt.event_name.toLowerCase() === value.toLowerCase(),
          ) || null
        );
      } else {
        // Value is IPreferedEvent
        const preferredEvent = value as IPreferedEvent;
        return (
          displayOptions?.find(
            (opt) => opt.event_uuid === preferredEvent?.event_uuid,
          ) || null
        );
      }
    }
  };

  // Handle single selection
  const handleSingleSelection = (newValue: IEvent | null) => {
    if (!newValue) {
      onSelect("", "", "");
      return;
    }

    // For single selection, pass event_name, event_uuid, and event_type
    onSelect(
      newValue.event_name,
      newValue.event_uuid || "",
      newValue.event_name,
    );
  };

  // Handle multiple selection
  const handleMultipleSelection = (newValue: IEvent[]) => {
    // Check if "Select All" was just selected
    const selectAllWasAdded = newValue.some(
      (item) => item.event_uuid === "__SELECT_ALL__",
    );
    const currentValue = value as IPreferedEvent[];
    const allWasSelected = currentValue?.some(
      (item) => item.event_uuid === "__SELECT_ALL__",
    );

    let finalSelection: IEvent[];

    if (selectAllWasAdded && !allWasSelected) {
      // "Select All" was just added - select all actual events (exclude SELECT_ALL_OPTION)
      finalSelection = options.slice(); // All actual events
    } else if (!selectAllWasAdded && allWasSelected) {
      // "Select All" was just removed - keep only the events that are still selected
      finalSelection = newValue.filter(
        (item) => item.event_uuid !== "__SELECT_ALL__",
      );
    } else {
      // Normal selection/deselection - filter out SELECT_ALL_OPTION
      finalSelection = newValue.filter(
        (item) => item.event_uuid !== "__SELECT_ALL__",
      );
    }

    // Return array of IPreferedEvent objects
    const preferredEvents: IPreferedEvent[] = finalSelection.map((item) => ({
      event_uuid: item.event_uuid || "",
      event_name: item.event_name,
      event_type: item.event_name, // event_type is same as event_name
    }));
    onSelect(preferredEvents);
  };
  const fetchSuggestion = async () => {
    if (fetched || !company.comapny.company_outlet_uuid) return;

    setLoading(true);
    try {
      const url = `/event/get-event?company_outlet_uuid=${company.comapny.company_outlet_uuid}`;
      const res = await api.get(url);
      // const data: IEvent[] = res.data.data;
      // setOptions(data);

      const data: IEvent[] = res.data.data;

      const filteredData = props.allowedEvents?.length
        ? data.filter((event) =>
            props.allowedEvents!.some(
              (allowed) =>
                allowed.toLowerCase() === event.event_name.toLowerCase(),
            ),
          )
        : data;

      setOptions(filteredData);

      setFetched(true); // ✅ mark as fetched
    } catch (error) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {heading && <CustomFormLabel>{heading}</CustomFormLabel>}
      <EnrAutoComplete
        multiple={multiple}
        fullWidth={fullWidth}
        disableCloseOnSelect={multiple}
        onFocus={fetchSuggestion}
        isHighlighted={isHighlighted}
        // Input (default renderInput handles label/error/loading)
        label={label}
        error={typeof error === "string" ? error : undefined}
        helperText={helperText}
        loading={loading}
        // Tag label for the default (single-value) tag rendering; the custom
        // renderTags below still wins because SELECT_ALL needs special handling.
        getTagLabel={(option) => option.event_name || ""}
        getOptionLabel={(option) => option.event_name || ""}
        options={displayOptions}
        value={getAutocompleteValue() as any}
        isOptionEqualToValue={(option, value) =>
          option.event_uuid === value.event_uuid
        }
        disabled={disabled}
        noOptionsText="No events available"
        onChange={(event, newValue) => {
          if (multiple) {
            handleMultipleSelection(newValue as IEvent[]);
          } else {
            handleSingleSelection(newValue as IEvent | null);
          }
        }}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props as any;
          const isSelectAll = option.event_uuid === "__SELECT_ALL__";

          if (isSelectAll && multiple) {
            // Custom rendering for "Select All" option
            return (
              <ListItem
                key={key}
                {...optionProps}
                sx={{
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: "#f5f5f5",
                  "&:hover": {
                    backgroundColor: "#eeeeee !important",
                  },
                  fontWeight: 600,
                }}
              >
                {multiple && (
                  <Checkbox checked={selected} sx={{ marginRight: 1 }} />
                )}
                <Typography sx={{ fontWeight: 600, color: "primary.main" }}>
                  {option.event_name}
                </Typography>
              </ListItem>
            );
          }

          // Normal option rendering
          return (
            <ListItem key={key} {...optionProps}>
              {multiple && (
                <Checkbox checked={selected} sx={{ marginRight: 1 }} />
              )}
              {option.event_name}
            </ListItem>
          );
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue
            .filter((option) => option.event_uuid !== "__SELECT_ALL__") // Don't show SELECT_ALL in chips
            .map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  label={option.event_name}
                  onDelete={disabled ? undefined : tagProps.onDelete}
                />
              );
            })
        }
      />
    </>
  );
};
